/**
 * @mention plumbing shared by the two internal-chat composers (lead drawer
 * `ActionForChat`, header `ChatDropdown`).
 *
 * The old detection was `text.match(/@(\w*)$/)` — it only saw an `@` at the
 * very END of the input, a single `\w` word, and it stopped the moment you
 * typed a space. Names here are "Arjun Liljo", so "@arjun l" killed the
 * dropdown, and editing mid-sentence never opened it. Everything below is
 * CARET-based: the token being typed is the `@…` immediately before the
 * cursor, on the same line, and may contain spaces.
 */

export interface MentionUser {
  id: number;
  name: string;
  role?: string;
}

export interface MentionRef {
  userId: number;
  name: string;
}

/** The `@query` token under the caret, or null when the caret isn't in one. */
export interface MentionToken {
  /** Index of the `@`. */
  start: number;
  /** Text after the `@` up to the caret (may contain spaces). */
  query: string;
}

const MAX_QUERY_LEN = 40;
const MAX_QUERY_WORDS = 3;

export function findMentionToken(
  text: string,
  caret: number,
): MentionToken | null {
  const upto = text.slice(0, Math.max(0, caret));
  const at = upto.lastIndexOf("@");
  if (at < 0) return null;
  // `@` must start a token: beginning of text or after whitespace / "(" etc.
  const before = at > 0 ? upto[at - 1] : " ";
  if (/[\w@]/.test(before)) return null;
  const query = upto.slice(at + 1);
  if (query.includes("\n")) return null;
  if (query.length > MAX_QUERY_LEN) return null;
  if (query.trim().split(/\s+/).length > MAX_QUERY_WORDS) return null;
  return { start: at, query };
}

/** Users matching the query — prefix matches first, then substring. */
export function filterMentionUsers<T extends MentionUser>(
  users: T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  const prefix: T[] = [];
  const rest: T[] = [];
  for (const u of users) {
    const name = (u.name || "").toLowerCase();
    if (!name.includes(q)) continue;
    // "prefix" = whole name or any word of it starts with the query
    if (name.startsWith(q) || name.split(/\s+/).some((w) => w.startsWith(q)))
      prefix.push(u);
    else rest.push(u);
  }
  return [...prefix, ...rest];
}

/**
 * Replace the `@query` token with `@Name ` and return the new text + caret.
 */
export function applyMention(
  text: string,
  caret: number,
  token: MentionToken,
  name: string,
): { text: string; caret: number } {
  const insert = `@${name} `;
  const next = text.slice(0, token.start) + insert + text.slice(caret);
  return { text: next, caret: token.start + insert.length };
}

/** Keep only the mentions whose `@Name` is still present in the text. */
export function mentionsPresentIn(
  text: string,
  mentions: MentionRef[],
): MentionRef[] {
  const seen = new Set<number>();
  return mentions.filter((m) => {
    if (seen.has(m.userId)) return false;
    if (!text.includes(`@${m.name}`)) return false;
    seen.add(m.userId);
    return true;
  });
}

/** Two-letter initials for an avatar chip. */
export function initialsOf(name: string): string {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * WhatsApp Backspace: with the caret right after a chosen `@Name`, the whole
 * token goes in one stroke. Returns the range to delete, or null when the
 * caret doesn't sit at the end of a mention (normal Backspace then applies).
 * Longest name wins, so "@Arjun Liljo" is not read as "@Arjun".
 */
export function mentionRangeEndingAt(
  text: string,
  caret: number,
  mentions: MentionRef[],
): { start: number; end: number } | null {
  const names = [...new Set(mentions.map((m) => m.name).filter(Boolean))].sort((a, b) => b.length - a.length);
  for (const name of names) {
    const token = `@${name}`;
    const start = caret - token.length;
    if (start < 0 || text.slice(start, caret) !== token) continue;
    // the token must start a word, as findMentionToken requires
    if (start > 0 && /[\w@]/.test(text[start - 1]!)) continue;
    return { start, end: caret };
  }
  return null;
}

/**
 * The composer text split into plain runs and chosen `@Name` tokens — what the
 * highlight layer behind the textarea draws. Concatenating `text` of every
 * segment gives the input back exactly, so the layer lines up with the box.
 */
export function mentionSegments(
  text: string,
  mentions: MentionRef[],
): { text: string; mention: boolean }[] {
  const names = [...new Set(mentions.map((m) => m.name).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!names.length || !text) return [{ text, mention: false }];
  const re = new RegExp(`(@(?:${names.map(escapeRegExp).join("|")}))(?![\\w])`, "g");
  return text
    .split(re)
    .map((part, i) => ({ text: part, mention: i % 2 === 1 }))
    .filter((s) => s.text !== "");
}
